import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import PhotoUpload from './PhotoUpload';
import { Plus, Trash2, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';

/**
 * BioEditor — single-person bio (prelate, supervisor, first lady).
 * Debounced save on all text fields.
 */
export function BioEditor({ slug, label }) {
  const [c, setC] = useState({});
  const [busy, setBusy] = useState(false);
  const timer = useRef(null); const pending = useRef(null);

  const load = async () => { try { const r = await api.get(`/site/page-content/${slug}`); setC(r.data || {}); } catch {} };
  useEffect(() => { load(); }, [slug]);

  const persist = (patch, { immediate } = {}) => {
    pending.current = patch;
    if (timer.current) clearTimeout(timer.current);
    const flush = async () => {
      const draft = pending.current; pending.current = null;
      if (!draft) return;
      setBusy(true);
      try { await api.patch(`/site/page-content/${slug}`, draft); await load(); toast.success('Saved · live'); }
      catch { toast.error('Save failed'); } finally { setBusy(false); }
    };
    if (immediate) return flush();
    timer.current = setTimeout(flush, 700);
  };
  const set = (k, v) => { setC(p => ({ ...p, [k]: v })); persist({ [k]: v }); };

  const paragraphs = c.bio_paragraphs || [];
  const setParagraph = (i, v) => {
    const next = [...paragraphs]; next[i] = v;
    setC(p => ({ ...p, bio_paragraphs: next }));
    persist({ bio_paragraphs: next });
  };
  const addParagraph = () => persist({ bio_paragraphs: [...paragraphs, ''] }, { immediate: true });
  const removeParagraph = (i) => persist({ bio_paragraphs: paragraphs.filter((_, j) => j !== i) }, { immediate: true });
  const moveP = (i, d) => {
    const s = i + d; if (s < 0 || s >= paragraphs.length) return;
    const next = [...paragraphs];
    [next[i], next[s]] = [next[s], next[i]];
    persist({ bio_paragraphs: next }, { immediate: true });
  };

  return (
    <div className="max-w-3xl bg-white border rounded-lg p-6 space-y-4">
      <h4 className="font-serif text-lg text-royal font-bold">{label} — Bio Page</h4>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <PhotoUpload label="Portrait photo" value={c.photo} onChange={(v) => set('photo', v)} category="leaders" />
          <div>
            <div className="text-sm font-semibold text-royal mb-1">Zoom ({c.photo_scale || 1}x)</div>
            <input type="range" min="0.8" max="3" step="0.05" value={c.photo_scale || 1}
              onChange={(e) => set('photo_scale', parseFloat(e.target.value))} className="w-full" />
          </div>
          <div>
            <div className="text-sm font-semibold text-royal mb-1">Vertical position ({c.photo_position || 'center top'})</div>
            <input type="range" min="0" max="100" step="1"
              value={parseInt(((c.photo_position || '50% 10%').split(' ')[1]) || 10)}
              onChange={(e) => set('photo_position', `50% ${e.target.value}%`)} className="w-full" />
          </div>
          <PhotoUpload label="Hero background image (optional)" value={c.hero_image} onChange={(v) => set('hero_image', v)} category="pages" />
        </div>
        <div className="space-y-3">
          <F label="Overline (small line above name)"><input value={c.overline || ''} onChange={(e) => set('overline', e.target.value)} className="w-full px-3 py-2 border rounded" /></F>
          <F label="Name"><input value={c.name || ''} onChange={(e) => set('name', e.target.value)} data-testid={`bio-${slug}-name`} className="w-full px-3 py-2 border rounded" /></F>
          <F label="Title / Rank"><input value={c.title || ''} onChange={(e) => set('title', e.target.value)} className="w-full px-3 py-2 border rounded" /></F>
          <F label="Subtitle (italic under title)"><input value={c.subtitle || ''} onChange={(e) => set('subtitle', e.target.value)} className="w-full px-3 py-2 border rounded" /></F>
        </div>
      </div>

      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-3">
          <h5 className="font-semibold text-royal">Bio paragraphs ({paragraphs.length})</h5>
          <button onClick={addParagraph} data-testid={`bio-${slug}-add-p`} className="text-xs px-3 py-1.5 bg-royal text-white rounded flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add Paragraph
          </button>
        </div>
        <div className="space-y-2">
          {paragraphs.map((p, i) => (
            <div key={i} className="flex gap-2 items-start">
              <textarea rows="3" value={p} onChange={(e) => setParagraph(i, e.target.value)} className="flex-1 px-3 py-2 border rounded text-sm" />
              <div className="flex flex-col gap-1">
                <button onClick={() => moveP(i, -1)} disabled={i === 0} className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => moveP(i, 1)} disabled={i === paragraphs.length - 1} className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                <button onClick={() => removeParagraph(i)} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t pt-4 space-y-3">
        <h5 className="font-semibold text-royal">Optional video (YouTube)</h5>
        <F label="YouTube URL (watch/, youtu.be/, or /embed/)"><input value={c.youtube_url || ''} onChange={(e) => set('youtube_url', e.target.value)} data-testid={`bio-${slug}-youtube`} placeholder="https://www.youtube.com/watch?v=..." className="w-full px-3 py-2 border rounded font-mono text-xs" /></F>
        <F label="Video section title"><input value={c.video_title || ''} onChange={(e) => set('video_title', e.target.value)} placeholder="A Word From the Prelate" className="w-full px-3 py-2 border rounded" /></F>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={c.show_video !== false} onChange={(e) => set('show_video', e.target.checked)}
            data-testid={`bio-${slug}-show-video`} className="w-4 h-4 accent-royal" />
          <span>Show video section on public page (unchecking hides it without deleting)</span>
        </label>
      </div>
      {busy && <div className="text-xs text-charcoal-muted italic flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</div>}
    </div>
  );
}

/**
 * LegacyEditor — array of former leaders.
 */
export function LegacyEditor() {
  const [c, setC] = useState({});
  const [busy, setBusy] = useState(false);
  const timer = useRef(null); const pending = useRef(null);
  const load = async () => { try { const r = await api.get('/site/page-content/legacy'); setC(r.data || {}); } catch {} };
  useEffect(() => { load(); }, []);

  const persist = (patch, { immediate } = {}) => {
    pending.current = patch;
    if (timer.current) clearTimeout(timer.current);
    const flush = async () => {
      const draft = pending.current; pending.current = null;
      if (!draft) return;
      setBusy(true);
      try { await api.patch('/site/page-content/legacy', draft); await load(); toast.success('Saved · live'); }
      catch { toast.error('Save failed'); } finally { setBusy(false); }
    };
    if (immediate) return flush();
    timer.current = setTimeout(flush, 700);
  };
  const leaders = (c.legacy_leaders || []).slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  const setField = (k, v) => { setC(p => ({ ...p, [k]: v })); persist({ [k]: v }); };
  const upd = (id, patch) => {
    const next = leaders.map(l => l.id === id ? { ...l, ...patch } : l);
    setC(p => ({ ...p, legacy_leaders: next }));
    persist({ legacy_leaders: next });
  };
  const add = () => {
    const order = leaders.length ? Math.max(...leaders.map(l => l.order ?? 0)) + 1 : 0;
    persist({ legacy_leaders: [...leaders, { id: `leg_${Date.now()}`, order, name: 'New Leader', title: '', photo: '', bio_paragraphs: [], youtube_url: '', show_video: true }] }, { immediate: true });
  };
  const remove = (id) => { if (!window.confirm('Remove this leader?')) return; persist({ legacy_leaders: leaders.filter(l => l.id !== id) }, { immediate: true }); };
  const move = (id, delta) => {
    const idx = leaders.findIndex(l => l.id === id);
    const swap = idx + delta;
    if (swap < 0 || swap >= leaders.length) return;
    const next = [...leaders];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    persist({ legacy_leaders: next.map((l, i) => ({ ...l, order: i })) }, { immediate: true });
  };

  return (
    <div className="space-y-5">
      <div className="bg-white border rounded-lg p-6 space-y-3">
        <h4 className="font-serif text-lg text-royal font-bold">Legacy — Hero &amp; Intro</h4>
        <PhotoUpload label="Hero background image" value={c.hero_image} onChange={(v) => setField('hero_image', v)} category="pages" />
        <F label="Hero heading"><input value={c.hero_heading || ''} onChange={(e) => setField('hero_heading', e.target.value)} className="w-full px-3 py-2 border rounded" placeholder="Legacy of Faith" /></F>
        <F label="Hero blurb"><textarea rows="2" value={c.hero_blurb || ''} onChange={(e) => setField('hero_blurb', e.target.value)} className="w-full px-3 py-2 border rounded" /></F>
      </div>

      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h4 className="font-serif text-lg text-royal font-bold">Legacy Leaders ({leaders.length})</h4>
          <p className="text-xs text-charcoal-muted mt-1">Add, edit, reorder, or hide former leaders. Each has an optional YouTube video that can be turned on/off.</p>
        </div>
        <button onClick={add} data-testid="legacy-add-leader" className="btn-royal text-sm py-2 px-4 flex items-center gap-1"><Plus className="w-4 h-4" /> Add Leader</button>
      </div>

      <div className="space-y-3">
        {leaders.map((l, i) => (
          <div key={l.id} data-testid={`legacy-editor-${l.id}`} className="bg-white border rounded-lg p-4 grid md:grid-cols-12 gap-4">
            <div className="md:col-span-4 space-y-2">
              <PhotoUpload label="Portrait" value={l.photo} onChange={(v) => upd(l.id, { photo: v })} category="leaders" />
              <div>
                <div className="text-xs font-semibold text-royal">Zoom ({l.photo_scale || 1}x)</div>
                <input type="range" min="0.8" max="3" step="0.05" value={l.photo_scale || 1} onChange={(e) => upd(l.id, { photo_scale: parseFloat(e.target.value) })} className="w-full" />
              </div>
              <div>
                <div className="text-xs font-semibold text-royal">Vertical position</div>
                <input type="range" min="0" max="100" step="1" value={parseInt(((l.photo_position || '50% 10%').split(' ')[1]) || 10)} onChange={(e) => upd(l.id, { photo_position: `50% ${e.target.value}%` })} className="w-full" />
              </div>
            </div>
            <div className="md:col-span-8 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input value={l.name || ''} onChange={(e) => upd(l.id, { name: e.target.value })} data-testid={`legacy-name-${l.id}`} placeholder="Full name" className="px-3 py-2 border rounded font-semibold" />
                <input value={l.title || ''} onChange={(e) => upd(l.id, { title: e.target.value })} placeholder="Title / rank" className="px-3 py-2 border rounded" />
              </div>
              <input value={l.years || ''} onChange={(e) => upd(l.id, { years: e.target.value })} placeholder="Years of service (e.g. 1978 – 2005)" className="w-full px-3 py-2 border rounded text-sm" />
              <textarea rows="5" value={(l.bio_paragraphs && l.bio_paragraphs.join('\n\n')) || l.bio || ''}
                onChange={(e) => upd(l.id, { bio_paragraphs: e.target.value.split(/\n\n+/).map(s => s.trim()).filter(Boolean) })}
                placeholder="Biography (paragraphs separated by a blank line)" className="w-full px-3 py-2 border rounded" />
              <div className="grid grid-cols-2 gap-2">
                <input value={l.youtube_url || ''} onChange={(e) => upd(l.id, { youtube_url: e.target.value })} data-testid={`legacy-youtube-${l.id}`} placeholder="YouTube URL (optional)" className="px-3 py-2 border rounded font-mono text-xs" />
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={l.show_video !== false} onChange={(e) => upd(l.id, { show_video: e.target.checked })} data-testid={`legacy-show-video-${l.id}`} className="w-4 h-4 accent-royal" />
                  Show video
                </label>
              </div>
              <div className="flex items-center gap-1 pt-1">
                <button onClick={() => move(l.id, -1)} disabled={i === 0 || busy} className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => move(l.id, 1)} disabled={i === leaders.length - 1 || busy} className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                <button onClick={() => remove(l.id)} className="ml-auto p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function F({ label, children }) { return (<div><div className="text-sm font-semibold text-royal mb-1">{label}</div>{children}</div>); }
